// Reset Password Page
import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, Eye, EyeOff, Loader2, CheckCircle, KeyRound, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const { isDarkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const validatePassword = (pwd) => {
    const checks = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd)
    };
    return checks;
  };

  const passwordChecks = validatePassword(password);
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error('Lien de réinitialisation invalide');
      setError(true);
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (!isPasswordValid) {
      toast.error('Le mot de passe ne respecte pas les critères de sécurité');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      toast.success('Mot de passe réinitialisé avec succès !');
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error('Le lien a expiré ou est invalide');
        setError(true);
      } else {
        toast.error('Erreur lors de la réinitialisation');
      }
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className={`min-h-screen flex items-center justify-center px-4 ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
        <Card className={`w-full max-w-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Lien invalide
            </h3>
            <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              Ce lien de réinitialisation est invalide ou a expiré.
            </p>
            <Link to="/forgot-password">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Demander un nouveau lien
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
      <Card className={`w-full max-w-md ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4">
            <KeyRound className="w-8 h-8 text-blue-500" />
          </div>
          <CardTitle className={isDarkMode ? 'text-white' : 'text-gray-900'}>
            Nouveau mot de passe
          </CardTitle>
          <CardDescription className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>
            Créez un nouveau mot de passe sécurisé
          </CardDescription>
        </CardHeader>

        <CardContent>
          {success ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Mot de passe modifié !
              </h3>
              <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.
              </p>
              <Link to="/login">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  Se connecter
                </Button>
              </Link>
            </div>
          ) : error ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Lien expiré
              </h3>
              <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                Ce lien de réinitialisation a expiré. Veuillez en demander un nouveau.
              </p>
              <Link to="/forgot-password">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  Demander un nouveau lien
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={`text-sm mb-1 block ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`pl-10 pr-10 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {/* Password strength indicators */}
                {password && (
                  <div className="mt-2 space-y-1">
                    {[
                      { key: 'length', label: 'Au moins 8 caractères' },
                      { key: 'uppercase', label: 'Une majuscule' },
                      { key: 'lowercase', label: 'Une minuscule' },
                      { key: 'number', label: 'Un chiffre' }
                    ].map(check => (
                      <div key={check.key} className="flex items-center gap-2 text-xs">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                          passwordChecks[check.key] ? 'bg-green-500' : 'bg-slate-600'
                        }`}>
                          {passwordChecks[check.key] && <CheckCircle className="w-3 h-3 text-white" />}
                        </div>
                        <span className={passwordChecks[check.key] 
                          ? 'text-green-500' 
                          : isDarkMode ? 'text-slate-400' : 'text-gray-500'
                        }>
                          {check.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className={`text-sm mb-1 block ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`pl-10 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                    required
                  />
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">Les mots de passe ne correspondent pas</p>
                )}
              </div>

              <Button 
                type="submit" 
                disabled={loading || !isPasswordValid || password !== confirmPassword}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Réinitialisation...</>
                ) : (
                  'Réinitialiser le mot de passe'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
